import { Grid, Paper, Typography, ToggleButtonGroup, ToggleButton, Tooltip, Select, MenuItem } from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import React, { useMemo, useState } from "react";
import CardSkeleton from "./CardSkeleton";
import NoDataAvailableCard from "./NoDataAvailableCard";
import GraphCard from "./GraphCard";
import StatTable from "./StatTable";
import CardFilter from "./CardFilter";

const CHART_COLORS = ["#4169D8", "#58B982"];

const getInvoicePaymentsChartOption = (baseOption, chartType) => {
  if (!baseOption) return null;

  const series = (baseOption.series || []).map((serie, index) => {
    const color = CHART_COLORS[index % CHART_COLORS.length];

    if (chartType === "bar") {
      const { smooth, symbol, symbolSize, showSymbol, lineStyle, areaStyle, ...rest } = serie;
      return {
        ...rest,
        type: "bar",
        barMaxWidth: 40,
        barGap: "15%",
        itemStyle: { color, borderRadius: [8, 8, 0, 0] },
      };
    }

    return {
      ...serie,
      type: "line",
      smooth: serie.smooth ?? 0.35,
      symbol: serie.symbol || "circle",
      symbolSize: serie.symbolSize || 6,
      showSymbol: true,
      lineStyle: { width: 3, ...serie.lineStyle, color },
      itemStyle: { color, borderColor: "#FFFFFF", borderWidth: 2 },
    };
  });

  return {
    ...baseOption,
    color: baseOption.color || CHART_COLORS,
    tooltip: {
      ...baseOption.tooltip,
      axisPointer: {
        ...baseOption.tooltip?.axisPointer,
        type: chartType === "bar" ? "shadow" : "cross",
      },
    },
    xAxis: {
      ...baseOption.xAxis,
      boundaryGap: chartType === "bar",
    },
    series,
  };
};

const InvoiceAndPaymentTrends = ({
  invVal,
  invDateType,
  onInvDateTypeChange,
  onInvPeriodChange,
  isLoading,
  invPaymentsData,
  invType,
  onInvTypeChange,
  filters = {},
  onFilterChange,
  currencies = [],
  cardFiltersDisabled = false,
}) => {
  const [invoicePaymentsChartType, setInvoicePaymentsChartType] = useState("line");
  const invoicePaymentsChartOptions = useMemo(
    () =>
      getInvoicePaymentsChartOption(
        invPaymentsData?.invoice_payments?.options,
        invoicePaymentsChartType
      ),
    [invPaymentsData?.invoice_payments?.options, invoicePaymentsChartType]
  );

  return (
    <div className="contract-analytics-wrapper">
      <div className="ca-page-header">
        <Typography fontSize="15px" fontFamily="Roboto-SemiBold !important">
          Invoice & Payment Trends by Charge Type{" "}
        </Typography>
      </div>
      <Grid container spacing={1.5} mx={1}>
        <Grid size={{ md: 12, xs: 12 }}>
          <Paper>
            <CardFilter
              disabled={cardFiltersDisabled}
							title={invPaymentsData?.invoice_payments?.title || 'Invoiced vs Paid'}
              titleLoading={isLoading?.invoice_payments}
              filter={filters.invoice_payments}
							onChange={(filter) => onFilterChange('invoice_payments', filter)}
              currencyOptions={currencies}
              actions={
                <div className="ca-header-actions">
                  <Select
                    size="small"
                    value={invType}
                    disabled={isLoading?.invoice_payments}
                    onChange={(e) => onInvTypeChange(e.target.value)}
                    sx={{ height: 25, fontSize: '12px', background: '#FFF' }}
                  >
                    <MenuItem value="1">Reserve</MenuItem>
                    <MenuItem value="2">Rental</MenuItem>
                    <MenuItem value="3">Penalty</MenuItem>
                    <MenuItem value="9">Ad Hoc</MenuItem>
                    <MenuItem value="10">Management Fee</MenuItem>
                  </Select>
                  <Select
                    size="small"
                    value={invDateType}
                    disabled={isLoading?.invoice_payments}
                    onChange={(e) => onInvDateTypeChange(e.target.value)}
                    sx={{ height: 25, fontSize: '12px', background: '#FFF' }}
                  >
                    <MenuItem value='invoice_date'>Invoice date</MenuItem>
                    <MenuItem value='due_date'>Due date</MenuItem>
                    <MenuItem value='payment_date'>Payment date</MenuItem>
                  </Select>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={invoicePaymentsChartType}
                    onChange={(e, value) => value && setInvoicePaymentsChartType(value)}
                    sx={{
                      height: 28,
                      "& .MuiToggleButton-root": {
                        px: 1,
                        py: 0.25,
                        border: "1px solid #E5E7EB",
                        color: "#6B7280",
                        "&.Mui-selected": {
                          backgroundColor: "#EEF2FF",
                          color: "#4169D8",
                          "&:hover": {
                            backgroundColor: "#E0E7FF",
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="bar">
                      <Tooltip title="Bar chart" arrow>
                        <BarChartRoundedIcon fontSize="small" />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="line">
                      <Tooltip title="Line chart" arrow>
                        <ShowChartRoundedIcon fontSize="small" />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              }
            />
            {isLoading?.invoice_payments ? (
              <CardSkeleton height="320px" className="ca-graph-container" />
            ) : (
              <>
                <div className="ca-graph-container">
                  {!invoicePaymentsChartOptions ? (
                    <NoDataAvailableCard
                      title="No utilization data available"
                      subtext="Try adjusting the date range"
                    />
                  ) : (
                    <GraphCard
                      option={invoicePaymentsChartOptions}
                      height="320px"
                      onChartClick={() => {}}
                    />
                  )}
                </div>
              </>
            )}
          </Paper>
        </Grid>
				<Grid size={{ md: 6, xs: 12 }} sx={{ display: 'flex' }}>
          <Paper className="mr-equal-card">
						<CardFilter disabled={cardFiltersDisabled} title="Receivables Ageing" filter={filters.invoice_receivables} onChange={(filter) => onFilterChange('invoice_receivables', filter)} currencyOptions={currencies} />
            {isLoading?.invoice_receivables ? (
              <CardSkeleton />
            ) : (
              <div className="ca-claim-block ca-card-body">
                {!invPaymentsData?.invoice_receivables?.rows?.length ? (
									<NoDataAvailableCard title='No  Receivables Ageing data available' subtext='Try adjusting the date range' />
                ) : (
									<div className='clm-aprv-trnard ca-scroll-table'>
										<StatTable columns={invPaymentsData?.invoice_receivables?.columns || []} rows={invPaymentsData?.invoice_receivables?.rows} />
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <Paper>
						<CardFilter disabled={cardFiltersDisabled} title="Payment Timeliness & Partial Payments" filter={filters.payment_timeliness} onChange={(filter) => onFilterChange('payment_timeliness', filter)} currencyOptions={currencies} />
            {isLoading?.payment_timeliness ? (
							<CardSkeleton height='250px' className='ca-graph-container' />
            ) : (
              <>
                <div className="ca-graph-container">
									<div style={{ height: '60px', padding: '5px 0px 0px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', }}>
                    <div>
											<Typography variant='h5' fontWeight={900}>{invPaymentsData?.payment_timeliness?.summary?.average_days_late ?? '-'}</Typography>
											<span className="ca-claim-subtext">Average days late</span>
                    </div>
                    <div>
											<Typography variant='h5' fontWeight={900}>{invPaymentsData?.payment_timeliness?.summary?.paid_in_instalments ?? '-'}</Typography>
											<span className="ca-claim-subtext">Paid in instalments</span>
                    </div>
                  </div>
                  {!invPaymentsData?.payment_timeliness?.options ? (
										<NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                  ) : (
										<GraphCard option={invPaymentsData?.payment_timeliness?.options} height='200px' onChartClick={() => { }} />
                  )}
                </div>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
	)
}

export default InvoiceAndPaymentTrends
